import { ValidatorFn, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { environment } from 'src/environments/environment';

export class AppConstants {
  static readonly phonePrefix = '5';
  static readonly version = `?v=${environment.version}`;
  static readonly phoneMaxLength = 9;
  static readonly phoneMinLength = 9;
  static readonly phonePlaceholder = '5XXXXXXXX';
  static readonly otpStartPlaceholder = '➤';

  static readonly phonePattern = '^5[0-9]{7}$';

  // pattern لرقم الهاتف (يدعم طول متغير حسب الدولة)
  static phonePatternFun(phoneLength?: number): ValidatorFn {
    if (phoneLength && phoneLength > 0) {
      // يسمح برقم يبدأ بـ 0 ثم العدد المطلوب، أو بدون 0 بطول من min إلى max
      const max = phoneLength;
      const min = Math.max(1, phoneLength - 1);
      const regex = new RegExp(`^(0\\d{${max}}|\\d{${min},${max}})$`);
      return Validators.pattern(regex);
    }
    // القيمة الافتراضية القديمة (9 أرقام تبدأ بـ 5)
    return Validators.pattern(/^5\d{8}$/);
  }

  static readonly otpMaxLength = 1;
  static readonly otpMinLength = 1;
  // عدد خانات رمز التحقق في الواجهة (٤ خانات)
  static readonly otpAllFields = Array(4);
  static readonly defaultOtpForSmsNotSupported = '9575';

  static readonly userNameMaxLength = 25;
  static readonly userNameMinLength = 3;

  static readonly fullNameMaxLength = 70;
  static readonly fullNameMinLength = 2;

  // الدومين الحالي للبريد
  static readonly micEmail = '@i-mal.com';
  static readonly attachmentsWithOutMessage = '$$$-Attachment Without Message-$$$';
  static readonly mailPassword = '##$$Test@';
  static readonly mailFullName = 'Not appointed yet';
  static readonly fetchLimitApp = 10;
  static readonly mailSize = '100M';

  static readonly subjectMaxLength = 100;

  // الحصول على طول رقم الهاتف حسب الدولة (min/max) بدون مفتاح الدولة
  static getPhoneLengthForCountry(country: any): { min: number; max: number } {
    const length = country?.phoneLength || 9;
    return { min: length, max: length };
  }

  // أخذ أول حرفين (أو أكثر) من الاسم لعرض الأفاتار مثلاً
  static getFirstLetters(text: string | null | undefined): string {
    if (!text) return '';
    return text
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  // أخذ عدد محدد من الحروف من الناتج السابق
  static getFirstLettersWithCount(firstLetters: string, count: number): string {
    if (!firstLetters) return '';
    return firstLetters.substring(0, Math.max(0, count)).toUpperCase();
  }

  // استخراج نوع الملف من الامتداد
  static getFileType(input: string): string {
    if (!input) return '';
    const ext = input.split('.').pop()?.toLowerCase() || '';
    return ext;
  }

  // تنسيق حجم الملف (بايت → KB/MB)
  static formatFileSize(input: string): string {
    const size = Number(input);
    if (isNaN(size) || size < 0) return '0 B';
    if (size < 1024) return `${size} B`;
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  }

  // إزالة نطاق البريد من الرقم/الإيميل لعرض الرقم فقط
  static removeMicKsaEmails(text: string): string {
    if (!text) return '';
    return text.replace(this.micEmail, '').replace(/<|>/g, '').trim();
  }

  // استخراج الأسماء من مصفوفة/نص عناوين من نوع: "الاسم <email@domain>"
  static extractNames(input: string | string[] | null | undefined): string {
    if (!input) return '';
    const parts = Array.isArray(input) ? input : [input];

    const names = parts
      .join(',')
      .split(',')
      .map((p) => p.trim())
      .filter((p) => !!p)
      .map((p) => {
        const match = p.match(/^(.*)<([^>]+)>$/);
        if (match) {
          return match[1].trim();
        }
        // إذا لم يوجد شكل <email> نحاول أخذ ما قبل @ كاسم تقريبي
        const atIndex = p.indexOf('@');
        if (atIndex > 0) {
          return p.substring(0, atIndex).trim();
        }
        return p;
      })
      .filter((n) => !!n);

    return names.join(', ');
  }

  // استخراج أول إيميل فقط من مصفوفة/نص عناوين
  // مثال: "الاسم <9665@i-mal.com>" أو مصفوفة من هذه القيم
  static extractEmails(input: string | string[] | null | undefined): string {
    if (!input) return '';
    const parts = Array.isArray(input) ? input : [input];

    const emails: string[] = [];
    parts
      .join(',')
      .split(',')
      .map((p) => p.trim())
      .filter((p) => !!p)
      .forEach((p) => {
        const match = p.match(/<([^>]+)>/);
        if (match) {
          emails.push(match[1].trim());
        } else if (p.includes('@')) {
          emails.push(p.trim());
        }
      });

    // نرجع أول إيميل موجود فقط (نمط الاستخدام الحالي يتوقع string)
    return emails[0] ?? '';
  }

  // تنسيق نص الدعوة/المحتوى من Markdown أو نص عادي إلى HTML آمن للعرض
  static formatInvitationHtmlMarkdown(body: string): SafeHtml | string {
    if (!body) return '';
    // إذا كان يحتوي على وسوم HTML بسيطة نتركه كما هو
    if (/<[a-z][\s\S]*>/i.test(body)) {
      return body;
    }
    // وإلا نحوله من Markdown إلى HTML
    return this.convertMarkdownToSafeHtml(body);
  }

  // تحويل Markdown إلى نص عادي (للاستخدام في الـ preview)
  static markdownToPlainText(body: string): string {
    if (!body) return '';
    // إزالة الوسوم HTML
    let text = body.replace(/<\/?[^>]+(>|$)/g, '');
    // إزالة بعض رموز الـ Markdown الشائعة
    text = text.replace(/[#*_`>-]/g, '');
    return text.trim();
  }

  // ضمان أن الرابط يبدأ بـ / حتى لا يتكرر الدومين
  static ensureStartsWithSlash(url: string): string {
    if (!url) return '/';
    return url.startsWith('/') ? url : `/${url}`;
  }

  // قص النص عند أول ظهور للحرف المحدد
  static cutTextAtChar(text: string, char: string): string {
    if (!text) return '';
    const index = text.indexOf(char);
    if (index === -1) return text;
    return text.substring(0, index);
  }

  // التحقق أن النص أرقام فقط
  static checkIsNumericText(text: string): boolean {
    if (!text) return false;
    return /^\d+$/.test(text);
  }

  // التحقق أن النص يبدأ بحرف/رقم معين
  static checkStartsWithChar(text: string, char: string): boolean {
    if (!text || !char) return false;
    return text.startsWith(char);
  }

  // إزالة حرف من بداية النص إذا كان موجوداً
  static removeStartChar(text: string, char: string): string {
    if (!text || !char) return text;
    return text.startsWith(char) ? text.substring(1) : text;
  }

  // إزالة جميع الأصفار من البداية (مثلاً 00966559038223 → 966559038223)
  static removeLeadingZeros(text: string): string {
    if (!text) return text;
    return text.replace(/^0+/, '');
  }

  // استخراج اسم المستخدم من الإيميل (قبل @)
  static getUsernameFromEmail(text: string): string {
    if (!text) return '';
    const atIndex = text.indexOf('@');
    if (atIndex === -1) return text;
    return text.substring(0, atIndex);
  }

  // Toolbar modules for rich text editor
  static readonly toolbarModules: Record<string, any> = {
    toolbar: {
      container: [
        [{ align: [] }, { direction: 'rtl' }],
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote'],
        ['clean'],
        ['attachment', 'image'],
      ],
      handlers: {
        attachment: function (this: any) {
          const quill = this.quill;
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = '*/*';
          input.click();

          input.onchange = () => {
            const files = input.files;
            if (!files || files.length === 0) return;

            const selectedFiles = Array.from(files);

            if (!this.attachments) {
              this.attachments = [];
            }
            this.attachments.push(...selectedFiles);

            selectedFiles.forEach((file) => {
              const range = quill.getSelection(true);

              if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                  quill.insertEmbed(range.index, 'image', e.target.result);
                  quill.setSelection(range.index + 1);
                };
                reader.readAsDataURL(file);
              } else {
                const icon = getFileIcon(file.name);
                quill.insertText(range.index, ` ${icon} ${file.name} `, {
                  bold: true,
                  color: '#0070C0',
                });
                quill.setSelection(range.index + file.name.length + 3);
              }
            });
          };

          function getFileIcon(fileName: string): string {
            const ext = fileName.split('.').pop()?.toLowerCase();
            switch (ext) {
              case 'pdf':
                return '📕';
              case 'doc':
              case 'docx':
                return '📄';
              case 'xls':
              case 'xlsx':
                return '📊';
              case 'ppt':
              case 'pptx':
                return '📽️';
              default:
                return '📎';
            }
          }
        },
      },
    },
  };

  static convertMarkdownToSafeHtml(markdown: string): SafeHtml {
    const html = marked.parse(markdown || '', { breaks: true });
    return html as SafeHtml;
  }

  // ترميز بيانات الجهاز إلى Base64 لاستخدامها في الـ API (بيئة المتصفح)
  static encodeDeviceDataToBase64(payload: any): string {
    try {
      const json = JSON.stringify(payload || {});
      if (typeof btoa === 'function') {
        return btoa(json);
      }
      // fallback بسيط إذا لم تتوفر btoa
      return '';
    } catch {
      return '';
    }
  }
}
